import { create } from '@thoughtspot/logger';
import { collectEvent, flushEvents, setCollectEventObserver, } from './event-collector';
import { UserAction } from './user-action';
const logger = create('impersonation-tracking');
const SESSION_RECORD_KEY = 'ts.impersonation.telemetry.session';
const TAB_MARKER_KEY = 'ts.impersonation.telemetry.tab';
const MAX_RECORD_AGE_MS = 24 * 60 * 60 * 1000;
export var ImpersonationSurface;
(function (ImpersonationSurface) {
    ImpersonationSurface["STANDALONE"] = "standalone";
    ImpersonationSurface["EMBED"] = "embed";
})(ImpersonationSurface || (ImpersonationSurface = {}));
export var ActAsEntryPoint;
(function (ActAsEntryPoint) {
    ActAsEntryPoint["USER_MGMT_TABLE"] = "user_mgmt_table";
    ActAsEntryPoint["USER_DETAILS_CARD"] = "user_details_card";
})(ActAsEntryPoint || (ActAsEntryPoint = {}));
export var ImpersonationDenialReason;
(function (ImpersonationDenialReason) {
    ImpersonationDenialReason["SCOPE_VIOLATION"] = "scope_violation";
    ImpersonationDenialReason["PRIVILEGE_CEILING"] = "privilege_ceiling";
    ImpersonationDenialReason["SELF_IMPERSONATION"] = "self_impersonation";
    ImpersonationDenialReason["FLAG_DISABLED"] = "flag_disabled";
    ImpersonationDenialReason["OTHER"] = "other";
})(ImpersonationDenialReason || (ImpersonationDenialReason = {}));
export var ImpersonationExitReason;
(function (ImpersonationExitReason) {
    ImpersonationExitReason["EXPLICIT"] = "explicit";
    ImpersonationExitReason["IDLE_TIMEOUT"] = "idle_timeout";
    ImpersonationExitReason["HARD_TIMEOUT"] = "hard_timeout";
    ImpersonationExitReason["PARENT_SESSION_EXPIRED"] = "parent_session_expired";
    ImpersonationExitReason["TAB_CLOSE"] = "tab_close";
})(ImpersonationExitReason || (ImpersonationExitReason = {}));
export var ImpersonationResumeTrigger;
(function (ImpersonationResumeTrigger) {
    ImpersonationResumeTrigger["PAGE_REFRESH"] = "page_refresh";
    ImpersonationResumeTrigger["NEW_TAB"] = "new_tab";
    ImpersonationResumeTrigger["NEW_WINDOW"] = "new_window";
    ImpersonationResumeTrigger["PRIVILEGE_RESTORED"] = "privilege_restored";
})(ImpersonationResumeTrigger || (ImpersonationResumeTrigger = {}));
export var ImpersonationBlockReason;
(function (ImpersonationBlockReason) {
    ImpersonationBlockReason["SPOTTER_DISABLED"] = "spotter_disabled";
    ImpersonationBlockReason["CONNECTOR_BLOCKED"] = "connector_blocked";
    ImpersonationBlockReason["TS_SYNC_DISABLED"] = "ts_sync_disabled";
})(ImpersonationBlockReason || (ImpersonationBlockReason = {}));
const emit = (eventKey, props) => {
    try {
        collectEvent(new UserAction(eventKey), props);
    }
    catch (e) {
        logger.info(e);
    }
};
const readStorage = (storage) => {
    try {
        return window[storage];
    }
    catch (e) {
        logger.info(e);
        return undefined;
    }
};
const readRecord = () => {
    var _a;
    try {
        const raw = (_a = readStorage('localStorage')) === null || _a === void 0 ? void 0 : _a.getItem(SESSION_RECORD_KEY);
        if (!raw) {
            return undefined;
        }
        const record = JSON.parse(raw);
        return (record === null || record === void 0 ? void 0 : record.session_id) ? record : undefined;
    }
    catch (e) {
        logger.info(e);
        return undefined;
    }
};
const writeRecord = (record) => {
    var _a;
    try {
        (_a = readStorage('localStorage')) === null || _a === void 0 ? void 0 : _a.setItem(SESSION_RECORD_KEY, JSON.stringify(record));
    }
    catch (e) {
        logger.info(e);
    }
};
const clearRecord = () => {
    var _a, _b;
    try {
        (_a = readStorage('localStorage')) === null || _a === void 0 ? void 0 : _a.removeItem(SESSION_RECORD_KEY);
        (_b = readStorage('sessionStorage')) === null || _b === void 0 ? void 0 : _b.removeItem(TAB_MARKER_KEY);
    }
    catch (e) {
        logger.info(e);
    }
};
const readTabMarker = () => {
    var _a, _b;
    try {
        return ((_b = (_a = readStorage('sessionStorage')) === null || _a === void 0 ? void 0 : _a.getItem(TAB_MARKER_KEY)) !== null && _b !== void 0 ? _b : undefined);
    }
    catch (e) {
        logger.info(e);
        return undefined;
    }
};
const writeTabMarker = (sessionId) => {
    var _a;
    try {
        (_a = readStorage('sessionStorage')) === null || _a === void 0 ? void 0 : _a.setItem(TAB_MARKER_KEY, sessionId);
    }
    catch (e) {
        logger.info(e);
    }
};
const newSessionId = () => {
    var _a, _b;
    try {
        const uuid = (_b = (_a = window.crypto) === null || _a === void 0 ? void 0 : _a.randomUUID) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (uuid) {
            return uuid;
        }
    }
    catch (e) {
        logger.info(e);
    }
    return `imp-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
};
const durationSec = (record, endedAt) => Math.max(0, Math.round((endedAt - record.started_at) / 1000));
const isFresh = (record) => Date.now() - record.started_at < MAX_RECORD_AGE_MS;
let isEmittingActionEvent = false;
export const trackActionPerformedDuringSession = (props) => {
    const record = readRecord();
    if (!record) {
        return;
    }
    const updated = { ...record, action_count: record.action_count + 1 };
    writeRecord(updated);
    isEmittingActionEvent = true;
    try {
        emit('ACTION_PERFORMED_DURING_SESSION', {
            session_id: updated.session_id,
            action_type: props.actionType,
            resource_type: props.resourceType,
            blocked: !!props.blocked,
            block_reason: props.blockReason,
        });
    }
    finally {
        isEmittingActionEvent = false;
    }
};
const observeCollectedEvent = (userAction, eventProps) => {
    var _a, _b, _c, _d;
    if (isEmittingActionEvent || ((_a = userAction.tags) === null || _a === void 0 ? void 0 : _a.includes('impersonation'))) {
        return;
    }
    trackActionPerformedDuringSession({
        actionType: userAction.name,
        resourceType: String((_d = (_b = eventProps.resourceType) !== null && _b !== void 0 ? _b : (_c = userAction.tags) === null || _c === void 0 ? void 0 : _c[0]) !== null && _d !== void 0 ? _d : 'unknown'),
    });
};
const handlePageHide = () => {
    const record = readRecord();
    if (record) {
        writeRecord({ ...record, last_seen_at: Date.now() });
    }
};
let isSessionWatcherInstalled = false;
const installSessionWatcher = () => {
    if (isSessionWatcherInstalled) {
        return;
    }
    isSessionWatcherInstalled = true;
    setCollectEventObserver(observeCollectedEvent);
    window.addEventListener('pagehide', handlePageHide);
};
const uninstallSessionWatcher = () => {
    if (!isSessionWatcherInstalled) {
        return;
    }
    isSessionWatcherInstalled = false;
    setCollectEventObserver(undefined);
    window.removeEventListener('pagehide', handlePageHide);
};
export const trackActAsUserClicked = (props) => emit('ACT_AS_USER_CLICKED', {
    acting_admin_id: props.actingAdminId,
    target_user_id: props.targetUserId,
    entry_point: props.entryPoint,
    org_id: props.orgId,
});
export const trackAccessRequestModalTextCopied = (props) => emit('ACCESS_REQUEST_MODAL_TEXT_COPIED', {
    acting_admin_id: props.actingAdminId,
    target_user_id: props.targetUserId,
    org_id: props.orgId,
    ticket_ref: props.ticketRef,
    surface: props.surface,
    embed_host_origin: props.embedHostOrigin,
});
export const trackImpersonationDenied = (props) => emit('IMPERSONATION_DENIED', {
    acting_admin_id: props.actingAdminId,
    target_user_id: props.targetUserId,
    denial_reason: props.denialReason,
});
export const trackImpersonationBannerDismissAttempted = () => {
    const record = readRecord();
    emit('IMPERSONATION_BANNER_DISMISS_ATTEMPTED', {
        session_id: record === null || record === void 0 ? void 0 : record.session_id,
    });
};
export const trackImpersonationPreferenceChanged = (props) => emit('IMPERSONATION_PREFERENCE_CHANGED', {
    user_id: props.userId,
    org_id: props.orgId,
    old_value: props.oldValue,
    new_value: props.newValue,
    changed_by: props.changedBy,
});
const emitExit = (record, exitReason, endedAt = Date.now()) => emit('IMPERSONATION_EXIT', {
    session_id: record.session_id,
    duration_sec: durationSec(record, endedAt),
    exit_reason: exitReason,
    action_count: record.action_count,
});
const reportAbandonedSession = (record) => {
    if (isFresh(record)) {
        emitExit(record, ImpersonationExitReason.TAB_CLOSE, record.last_seen_at);
    }
    clearRecord();
};
export const startImpersonationTelemetrySession = (props) => {
    const previous = readRecord();
    if (previous) {
        reportAbandonedSession(previous);
    }
    const record = {
        session_id: newSessionId(),
        started_at: Date.now(),
        action_count: 0,
        acting_admin_id: props.actingAdminId,
        target_user_id: props.targetUserId,
        org_id: props.orgId,
        ticket_ref: props.ticketRef,
        surface: props.surface,
        embed_host_origin: props.embedHostOrigin,
    };
    writeRecord(record);
    writeTabMarker(record.session_id);
    emit('IMPERSONATION_START', {
        session_id: record.session_id,
        acting_admin_id: record.acting_admin_id,
        target_user_id: record.target_user_id,
        org_id: record.org_id,
        ticket_ref: record.ticket_ref,
        surface: record.surface,
        embed_host_origin: record.embed_host_origin,
    });
    flushEvents();
    return record.session_id;
};
export const resumeImpersonationTelemetrySession = () => {
    const record = readRecord();
    if (!record) {
        return undefined;
    }
    installSessionWatcher();
    const isSameTab = readTabMarker() === record.session_id;
    if (record.last_seen_at) {
        const { last_seen_at: _lastSeenAt, ...rest } = record;
        writeRecord(rest);
    }
    writeTabMarker(record.session_id);
    emit('IMPERSONATION_SESSION_RESUMED', {
        session_id: record.session_id,
        resume_trigger: isSameTab
            ? ImpersonationResumeTrigger.PAGE_REFRESH
            : ImpersonationResumeTrigger.NEW_TAB,
    });
    return record.session_id;
};
export const endImpersonationTelemetrySession = (exitReason) => {
    const record = readRecord();
    uninstallSessionWatcher();
    if (!record) {
        return;
    }
    clearRecord();
    emitExit(record, exitReason);
    flushEvents();
};
export const getImpersonationTelemetrySessionId = () => { var _a; return (_a = readRecord()) === null || _a === void 0 ? void 0 : _a.session_id; };
//# sourceMappingURL=impersonation-tracking.js.map